/**
 * Detects environment (iframe vs standalone), wires up the host-bridge
 * IframeClient when embedded, and applies the host's `init` payload to a
 * single HostState.
 */
import { useEffect, useState } from 'react';

import { IframeClient } from '@rendever/host-bridge/iframe';

import { isEmbedded } from './isEmbedded';
import type { HostConfig, HostState } from './types';

const APP_VERSION = '0.1.0';

const DEFAULT_STANDALONE_CONFIG: HostConfig = {
  firebaseIdToken: null,
  residentId: null,
  buildingId: undefined,
  locale: 'en-US',
  surface: 'standalone',
  qualityTier: 'high',
  inputMode: 'touch',
  accessibility: {
    textSize: 'standard',
    highContrast: false,
    reduceMotion: false,
    captionsOn: false,
    audioOn: true,
  },
  isDemo: false,
  appVersion: APP_VERSION,
};

function parseAllowedOrigins(): string[] {
  const raw = import.meta.env.VITE_ALLOWED_HOST_ORIGINS as string | undefined;
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function getTargetOrigin(): string | undefined {
  const raw = import.meta.env.VITE_HOST_TARGET_ORIGIN as string | undefined;
  return raw && raw.trim() ? raw.trim() : undefined;
}

/**
 * Builds the initial state synchronously so first render isn't a flicker.
 */
function initialState(): HostState {
  const mode: HostState['mode'] = isEmbedded() ? 'iframe' : 'standalone';
  return {
    mode,
    config: DEFAULT_STANDALONE_CONFIG,
    client: null,
    awaitingInit: mode === 'iframe',
  };
}

export function useHostBridge(): HostState {
  const [state, setState] = useState<HostState>(initialState);

  useEffect(() => {
    if (state.mode !== 'iframe') return;

    const allowedOrigins = parseAllowedOrigins();
    if (allowedOrigins.length === 0) {
      console.warn(
        '[useHostBridge] No VITE_ALLOWED_HOST_ORIGINS configured. ' +
          'Embedded mode without origin allow-list — falling through to standalone defaults.',
      );
      setState((prev) => ({ ...prev, awaitingInit: false }));
      return;
    }

    const targetOrigin = getTargetOrigin();
    const client = new IframeClient({
      allowedOrigins,
      ...(targetOrigin ? { targetOrigin } : {}),
    });

    const offInit = client.on('init', (msg) => {
      const p = msg.payload;
      setState((prev) => ({
        ...prev,
        awaitingInit: false,
        config: {
          firebaseIdToken: p.firebaseIdToken,
          residentId: p.residentId,
          buildingId: p.buildingId,
          locale: p.locale,
          surface: p.surface,
          qualityTier: p.qualityTier,
          inputMode: p.inputMode,
          accessibility: p.accessibility,
          isDemo: p.isDemo,
          appVersion: APP_VERSION,
        },
      }));
    });

    const offConfig = client.on('config_update', (msg) => {
      const patch = msg.payload;
      setState((prev) => {
        const nextAccessibility = { ...prev.config.accessibility };
        if (patch.accessibility) {
          const a = patch.accessibility;
          if (a.textSize !== undefined) nextAccessibility.textSize = a.textSize;
          if (a.highContrast !== undefined) nextAccessibility.highContrast = a.highContrast;
          if (a.reduceMotion !== undefined) nextAccessibility.reduceMotion = a.reduceMotion;
          if (a.captionsOn !== undefined) nextAccessibility.captionsOn = a.captionsOn;
          if (a.audioOn !== undefined) nextAccessibility.audioOn = a.audioOn;
        }
        return {
          ...prev,
          config: {
            ...prev.config,
            ...(patch.qualityTier ? { qualityTier: patch.qualityTier } : null),
            ...(patch.inputMode ? { inputMode: patch.inputMode } : null),
            accessibility: nextAccessibility,
          },
        };
      });
    });

    setState((prev) => ({ ...prev, client }));
    client.ready({ appVersion: APP_VERSION, supportedFeatures: [] });

    return () => {
      offInit();
      offConfig();
      client.dispose();
    };
    // Only re-run if mode flips (it won't).
     
  }, [state.mode]);

  return state;
}
